// src/utils/poLines.js
// D-291 (Stabilization P3c) - the rules for one purchase-order line, in ONE place.
//
// WHY THIS FILE EXISTS
// Before P3c the two PO screens (Direct PO and Assign Vendor) each built their
// lines their own way: a silent "18" GST default, an empty GST or rate box
// saved as 0, the agreed rate stored in `rate` on one path and in `base_rate`
// on the other. Now purchaseOrderService checks every line here before a PO
// is created or amended and refuses with the item's name when a line is
// wrong. The screens show the same checks early, but this file decides.
//
// Rules (Ravi, 20 Oct):
//   - unit required (P3c-7, 21 Oct) - a blank unit is refused
//   - quantity > 0; counted units (Nos, Set, Pcs ...) whole numbers only,
//     measured units (Mtr, RM, SqM, Kg ...) up to 2 decimals (units.js, P3c-6)
//   - rate before GST > 0 (up to 2 decimals), OR the line is ticked
//     "Rate not agreed yet" -> base_rate saved as 0 on purpose
//   - GST % REQUIRED, 0 to 40 - an empty box is refused, never read as 0;
//     an Unregistered vendor cannot charge GST -> every line must be 0
//   - D-325 (Ravi, 2 Nov): on a "Rate not agreed yet" line GST % may stay blank
//     = decided on the vendor's bill, saved as null (never 0). 0 means the item
//     truly has no GST. The bill then requires it (billPlan.js checkGst).
//   - HSN optional; if typed: 4, 6 or 8 digits (checkHsn)
//   - discount: % (0 to 100) or flat Rs. for the line (not more than the line amount)
// The saved line gets rate = base_rate (the agreed rate). The store GRN later
// overwrites `rate` with the GRN (stock) rate - never this file.
//
// Pure (no Firebase) -> covered by src/utils/poLines.test.js (Jest).

import { checkHsn, MAX_GST_PERCENT, roundHalfUp } from "./gstBill";
import { checkQtyForUnit, hasUnit } from "./units";
import { itemNameKey, matchItem } from "./itemMatch";

// P3c-8: a typed item name that is EXACTLY one Item Master item's name
// (case / extra spaces ignored) is that item - the line links to it as if it
// was clicked in the list. 0 or 2+ matches -> null (stays a custom line).
// P4-1: names compared by itemNameKey() in itemMatch.js ("250 mm" = "250mm").
export const normaliseItemName = (name) => String(name ?? "").toLowerCase().trim().replace(/\s+/g, " ");
export function findExactNameMatch(masterList, typedName) {
  if (itemNameKey(typedName).length < 2) return null;
  return matchItem(masterList, { item_name: typedName }).item;
}

// "" / null / undefined -> NaN, so an empty box is never read as 0.
const num = (v) => (v === "" || v === null || v === undefined ? NaN : Number(v));
const atMost = (n, places) => roundHalfUp(n, places) === n;

const qtyOf = (line) =>
  num(line?.ordered_qty !== undefined && line?.ordered_qty !== "" ? line.ordered_qty : line?.qty);

// The agreed rate before GST. Old Direct PO lines carried it only in `rate`.
const baseRateOf = (line) => {
  const b = num(line?.base_rate);
  return Number.isFinite(b) ? b : num(line?.rate);
};

/**
 * @param {Array}  lines  the PO lines (screen or saved); cancelled lines are
 *                        passed through unchanged and not checked
 * @param {Object} [opts]
 * @param {boolean} [opts.vendorUnregistered] true -> every line's GST must be 0
 * @returns {{ ok: boolean, errors: string[], lines: Array }}
 *   lines = the same lines, cleaned for saving (numbers, hsn_code, rate = base_rate).
 *   When ok is false the caller must not save anything.
 */
export function checkPOLines(lines, { vendorUnregistered = false } = {}) {
  const errors = [];
  const list = Array.isArray(lines) ? lines : [];

  const out = list.map((line, i) => {
    if (line?._cancelled) return { ...line };
    const name = String(line?.item_name || "").trim();
    const label = name ? `"${name}"` : `Line ${i + 1}`;
    if (!name) errors.push(`${label}: item name is missing`);

    // P3c-7: every line needs a unit (counted vs measured is decided by it).
    if (!hasUnit(line?.unit)) errors.push(`${label}: unit is missing - pick a unit`);

    const qty = qtyOf(line);
    if (!(qty > 0)) errors.push(`${label}: quantity must be more than 0`);
    else {
      const unitCheck = checkQtyForUnit(qty, line?.unit);
      if (!unitCheck.ok) errors.push(`${label}: ${unitCheck.reason}`);
    }

    const notAgreed = line?.rate_not_agreed === true;
    const rate = notAgreed ? 0 : baseRateOf(line);
    if (!notAgreed) {
      if (!(rate > 0)) errors.push(`${label}: enter the rate before GST, or tick "Rate not agreed yet"`);
      else if (!atMost(rate, 2)) errors.push(`${label}: rate can have at most 2 decimals`);
    }

    // Only a truly blank box counts - a wrong value on a ticked line is still refused.
    const gstRaw = line?.gst_percent;
    const gstBlank = gstRaw === "" || gstRaw === null || gstRaw === undefined;
    const gst = num(gstRaw);
    if (notAgreed && gstBlank) {
      // D-325: decided on the vendor's bill - saved as null below.
    } else if (!Number.isFinite(gst)) errors.push(`${label}: GST % is required (type 0 only if this item truly has no GST)`);
    else if (gst < 0 || gst > MAX_GST_PERCENT) errors.push(`${label}: GST % must be between 0 and ${MAX_GST_PERCENT}`);
    else if (!atMost(gst, 2)) errors.push(`${label}: GST % can have at most 2 decimals`);
    else if (vendorUnregistered && gst !== 0) errors.push(`${label}: this vendor is unregistered - GST must be 0`);

    const hsn = checkHsn(line?.hsn_code);
    if (!hsn.ok) errors.push(`${label}: ${hsn.reason}`);

    const discountType = line?.discount_type === "flat" ? "flat" : "percent";
    const dRaw = line?.discount_value;
    const discount = dRaw === "" || dRaw === null || dRaw === undefined ? 0 : Number(dRaw);
    if (!Number.isFinite(discount) || discount < 0) errors.push(`${label}: discount cannot be negative`);
    else if (!atMost(discount, 2)) errors.push(`${label}: discount can have at most 2 decimals`);
    else if (discountType === "percent" && discount > 100) errors.push(`${label}: discount % cannot be more than 100`);
    else if (discountType === "flat" && rate > 0 && qty > 0 && discount > roundHalfUp(qty * rate, 2)) {
      errors.push(`${label}: discount is more than the line amount`);
    }

    return {
      ...line,
      item_name:       name,
      qty,
      ordered_qty:     qty,
      base_rate:       rate,
      rate,
      rate_not_agreed: notAgreed,
      gst_percent:     Number.isFinite(gst) ? gst : null, // null only on a ticked line; never NaN
      hsn_code:        hsn.hsn,
      discount_type:   discountType,
      discount_value:  Number.isFinite(discount) ? discount : 0,
    };
  });

  return { ok: errors.length === 0, errors, lines: out };
}